<!--
  - Copyright (c) 2025. Haodong JU
  -->

<script>

import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import Asset, {
  adjustInventory,
  assetType,
  distributeAssetToUsersList
} from '@/model/dataLayer/api/MembershipSystem/Asset'
import { VSelect } from 'vuetify/lib/components'
import UserMembership from '@/model/dataLayer/api/MembershipSystem/UserMembership'
import DateSelector from '@/views/AadenPages/settings/reserveSetting/openTime/DateSelector.vue'
import { today } from '@/model/utils/DateRepository'
import Dish from '@/model/dataLayer/api/dish/Dish'
import { showErrorMessage, showSuccessMessage } from '@/model/utils/utils'
import { checkRedemption, createAssetRedemption } from '@/model/dataLayer/api/MembershipSystem/AssetRedemption'
import { mapMutations } from 'vuex'
import dayjs from 'dayjs'
import AssetLog from '@/model/dataLayer/api/MembershipSystem/AssetLog'
import UserMembershipChild from '@/model/dataLayer/api/MembershipSystem/UserMembershipChild'
import { useBaseFormDialogStore } from '@/model/baseForm/baseFormDialogStore'
import IKUtils from 'innerken-js-utils'
import { createAssetFormSchema } from '@/model/baseForm/assetFormSchemas'
import AssetDetailDialog from './AssetDetailDialog.vue'
import { openMemberSelectorDialog } from '@/components/MemberSelector'

export default {
  name: 'AssetPage',
  components: {
    IkDataTable,
    AssetDetailDialog
  },
  data () {
    return {
      note: '',
      assetLogModel: AssetLog,
      showAssetLog: false,
      model: Asset,
      userModel: UserMembershipChild,
      editObject: {
        assetType: 'VOUCHER'
      },
      allMemberUser: [],
      allDishes: [],
      assetType,
      showAssetDetailDialog: false,
      selectedAssetId: null
    }
  },
  computed: {
    assetFormSchema () {
      return createAssetFormSchema({
        assetTypeOptions: this.assetType,
        allDishes: this.allDishes
      })
    },
    inventorySchemas () {
      return [
        {
          key: 'desiredInventory',
          name: '实际库存',
          hint: `(原库存为:${this.editObject?.currentStock ?? 0})`,
          required: true,
          componentProps: {
            type: 'number',
            rules: [v => v !== '' || '库存不能为空', v => !isNaN(Number(v)) || '请输入有效数字']
          }
        }
      ]
    },
    redemptionCodeSchemas () {
      return [
        {
          key: 'single',
          name: '单个/批量',
          hint: '单个或批量创建兑换码',
          component: VSelect,
          default: true,
          tag: 'basic',
          componentProps: {
            items: [
              { text: '单个', value: true },
              { text: '批量', value: false }
            ]
          }
        },
        {
          key: 'code',
          name: 'code',
          hint: '如果单独创建,在这里输入指定兑换码,批量生成则不用填写',
          default: '',
          required: true,
          tag: 'basic',
          componentProps: {
            placeholder: '请输入兑换码',
            rules: [v => !v || /^[A-Za-z0-9-_]+$/.test(v) || '兑换码只能包含字母、数字、连字符和下划线']
          },
          preconditions: { single: true }
        },
        {
          key: 'count',
          name: 'count',
          hint: '如果批量生成,在这里输入指定数量,请不要填写Code',
          default: '',
          required: true,
          tag: 'basic',
          componentProps: {
            type: 'number',
            placeholder: '请输入生成数量',
            rules: [
              v => !v || !isNaN(Number(v)) || '请输入有效数字',
              v => !v || Number(v) > 0 || '数量必须大于0',
              v => !v || Number(v) <= 1000 || '单次最多生成1000个'
            ]
          },
          preconditions: { single: false }
        },
        {
          key: 'maxUsageCount',
          name: 'maxUsageCount',
          hint: '最大使用次数',
          default: 1,
          required: true,
          tag: 'settings',
          componentProps: {
            type: 'number',
            placeholder: '请输入最大使用次数',
            rules: [
              v => !!v || '最大使用次数不能为空',
              v => Number(v) > 0 || '最大使用次数必须大于0'
            ]
          }
        },
        {
          key: 'expiresAt',
          name: 'expiresAt',
          component: DateSelector,
          hint: '过期时间',
          required: false,
          tag: 'settings',
          componentProps: {
            min: today
          }
        },
        {
          key: 'note',
          name: 'note',
          hint: '备注信息',
          required: false,
          tag: 'settings',
          default: '',
          componentProps: {
            placeholder: '请输入备注信息'
          }
        }
      ]
    },
    configSchemas () {
      return [
        {
          key: 'type',
          name: 'type',
          hint: '资产类型',
          default: this.editObject?.assetType,
          tag: 'basic',
          required: true
        },
        // Voucher specific settings
        {
          key: 'maxLimit',
          name: 'maxLimit',
          hint: '最大限制数量',
          tag: 'voucher',
          required: false,
          componentProps: {
            type: 'number',
            placeholder: '请输入最大限制数量',
            rules: [v => !v || Number(v) > 0 || '限制数量必须大于0']
          },
          preconditions: { type: 'VOUCHER' }
        },
        {
          key: 'usageMinAmount',
          name: 'usageMinAmount',
          hint: '最低使用金额',
          tag: 'voucher',
          required: false,
          componentProps: {
            type: 'number',
            placeholder: '请输入最低使用金额',
            rules: [v => !v || Number(v) >= 0 || '最低使用金额不能为负数']
          },
          preconditions: { type: 'VOUCHER' }
        },
        {
          key: 'maxDiscountAmount',
          name: 'maxDiscountAmount',
          hint: '最大折扣金额',
          tag: 'voucher',
          required: false,
          componentProps: {
            type: 'number',
            placeholder: '请输入最大折扣金额',
            rules: [v => !v || Number(v) > 0 || '折扣金额必须大于0']
          },
          preconditions: { type: 'VOUCHER' }
        },
        {
          key: 'discountStr',
          name: 'discountStr',
          hint: '折扣值（如：0.8表示8折）',
          tag: 'voucher',
          required: false,
          componentProps: {
            placeholder: '请输入折扣值',
            rules: [
              v => !v || !isNaN(Number(v)) || '请输入有效数字',
              v => !v || (Number(v) > 0 && Number(v) <= 1) || '折扣值必须在0-1之间'
            ]
          },
          preconditions: { type: 'VOUCHER' }
        },
        {
          key: 'isDiscountLimitedToHighestValueItem',
          name: '限制折扣应用于最高价值商品',
          component: VSelect,
          tag: 'voucher',
          required: false,
          default: false,
          componentProps: {
            items: [
              { text: '是', value: true },
              { text: '否', value: false }
            ]
          },
          preconditions: { type: 'VOUCHER' }
        },
        {
          key: 'productCodes',
          name: 'productCodes',
          hint: '适用产品',
          component: VSelect,
          tag: 'voucher',
          required: false,
          componentProps: {
            placeholder: '请选择适用产品',
            multiple: true,
            items: this.allDishes.map(it => {
              it.text = it.dishName
              it.value = it.code
              return it
            })
          },
          preconditions: { type: 'VOUCHER' }
        },
        {
          key: 'activeSince',
          name: 'activeSince',
          hint: '生效日期',
          component: DateSelector,
          tag: 'dates',
          required: true,
          componentProps: {
            min: today
          },
          preconditions: { type: 'VOUCHER' }
        },
        {
          key: 'expiredAt',
          name: 'expiredAt',
          hint: '过期日期',
          component: DateSelector,
          tag: 'dates',
          required: true,
          componentProps: {
            min: today
          },
          preconditions: { type: 'VOUCHER' }
        },
        // Point exchange settings
        {
          key: 'point',
          name: 'point',
          hint: '积分值',
          tag: 'point',
          required: true,
          componentProps: {
            type: 'number',
            placeholder: '请输入积分值',
            rules: [
              v => !!v || '积分值不能为空',
              v => Number(v) > 0 || '积分值必须大于0'
            ]
          },
          preconditions: { type: 'POINT_EXCHANGE' }
        }
      ]
    }
  },
  async mounted () {
    await this.reload()
  },
  methods: {
    async sendAssetBatch () {
      const ids = this.$refs.userTable.selectedItems.map(item => item.userId)
      try {
        await distributeAssetToUsersList(this.editObject.id, { userIds: ids, note: this.note })
        showSuccessMessage('成功')
        this.note = ''
        this.showDistributedVoucherDialog = false
      } catch (error) {
        console.error('Error distributing asset:', error)
        showErrorMessage(error.message || '分发资产失败')
      }
    },
    getCurrentColor (action) {
      const colorMap = {
        CREATE: 'info',
        USE: '#4CAF50',
        DISTRIBUTE: '#9C27B0',
        EXPIRE: '#F44336',
        UPDATE: '#FFC107',
        REVOKE: '#9E9E9E'
      }
      return colorMap[action] || '#FF9800'
    },
    async openInventoryDialog (item) {
      // Deep copy the item to avoid modifying the original
      this.editObject = IKUtils.deepCopy(item)

      // Check if this asset has unlimited supply
      if (item.unlimitedSupply) {
        this.showErrorDialog('无限供应的资产不需要调整库存')
        return
      }

      const store = useBaseFormDialogStore()

      // Define the schema for the inventory adjustment dialog
      const schema = {
        title: '调整库存',
        subtitle: `调整 ${item.name || '资产'} 的库存`,
        schemas: this.inventorySchemas
      }

      // Open the dialog and wait for the result
      const result = await store.editItem(schema)

      // Handle the result
      if (result) {
        await this.saveInventory(result)
      }
    },
    async saveInventory (info) {
      try {
        await adjustInventory(this.editObject?.id, info)
        showSuccessMessage('成功！')
        await this.$refs.table.reload()
      } catch (error) {
        console.error('Error adjusting inventory:', error)
        showErrorMessage(error.message || '调整库存失败')
      }
    },
    async openAssetForm (asset = null) {
      const store = useBaseFormDialogStore()

      // Create a default asset object if none is provided
      let editingAsset = null

      if (asset) {
        // Deep copy the asset to avoid modifying the original
        editingAsset = IKUtils.deepCopy(asset)

        // Determine if this asset has unlimited supply
        if (editingAsset.unlimitedSupply === undefined) {
          // If unlimitedSupply is not set, determine based on currentStock
          editingAsset.unlimitedSupply = editingAsset.currentStock >= 999999
        }

        // Parse assetConfig if it exists
        if (editingAsset.assetConfig) {
          try {
            const config = JSON.parse(editingAsset.assetConfig)

            // Handle product codes for vouchers
            if (editingAsset.assetType === 'VOUCHER' && config.productCodes) {
              config.productCodes = config.productCodes.split(',')
            }

            // Merge config into editingAsset
            editingAsset = {
              ...editingAsset,
              ...config
            }
          } catch (e) {
            console.error('Error parsing assetConfig:', e)
            showErrorMessage('解析资产配置失败: ' + (e.message || '未知错误'))
          }
        }
      }

      // Define the schema for the asset form dialog
      const schema = {
        title: asset ? '编辑资产' : '添加资产',
        subtitle: asset ? `编辑 ${asset.name || '资产'}` : '创建新的资产',
        schemas: this.assetFormSchema
      }

      // Open the dialog and wait for the result
      const result = await store.editItem(schema, editingAsset)

      // Handle the result
      if (result) {
        await this.saveAsset(result)
      }
    },

    createNewAsset () {
      this.openAssetForm()
    },

    async deleteItem (item) {
      try {
        await Asset.remove(item.id)
        showSuccessMessage('资产删除成功')
        await this.$refs.table.reload()
      } catch (error) {
        console.error('Error deleting asset:', error)
        showErrorMessage(error.message || '删除资产失败')
      }
    },

    openAssetDetail (item) {
      this.selectedAssetId = item.id
      this.showAssetDetailDialog = true
    },

    handleEditAsset (asset) {
      this.openAssetForm(asset)
    },

    handleDistributeAsset (asset) {
      this.distributedVoucher(asset)
    },

    handleCreateRedemption (asset) {
      this.createRedemption(asset)
    },

    handleAssetDeleted () {
      this.$refs.table.reload()
    },

    async saveAsset (formData) {
      try {
        // Handle inventory based on unlimitedSupply setting
        if (formData.unlimitedSupply) {
          // If unlimited supply, set high values for inventory
          formData.initialStock = 999999
          formData.currentStock = 999999
        } else {
          // If limited supply, set both initialStock and currentStock to the same value
          formData.currentStock = formData.initialStock
        }

        // Process config based on asset type
        if (formData.assetType === 'VOUCHER') {
          const voucherConfig = {
            type: formData.assetType,
            maxLimit: formData.maxLimit,
            usageMinAmount: formData.usageMinAmount,
            maxDiscountAmount: formData.maxDiscountAmount,
            discountStr: formData.discountStr,
            productCodes: Array.isArray(formData.productCodes) ? formData.productCodes.join(',') : '',
            activeSince: dayjs(formData.activeSince).format('YYYY-MM-DD') + 'T00:00:00',
            expiredAt: dayjs(formData.expiredAt).format('YYYY-MM-DD') + 'T00:00:00',
            isDiscountLimitedToHighestValueItem: formData.isDiscountLimitedToHighestValueItem || false
          }
          formData.assetConfig = JSON.stringify(voucherConfig)
        } else if (formData.assetType === 'POINT_EXCHANGE') {
          const pointConfig = {
            type: formData.assetType,
            point: formData.point
          }
          formData.assetConfig = JSON.stringify(pointConfig)
        } else if (formData.assetType === 'PHYSICAL_REWARD') {
          const physicalConfig = {
            type: formData.assetType
          }
          formData.assetConfig = JSON.stringify(physicalConfig)
        }

        // Create or update the asset
        if (formData.id) {
          await Asset.edit(formData)
          showSuccessMessage('资产更新成功')
        } else {
          await Asset.add(formData)
          showSuccessMessage('资产创建成功')
        }

        // Reload the data table to show the new/updated asset
        if (this.$refs.table) {
          await this.$refs.table.reload()
        }
      } catch (error) {
        console.error('Error saving asset:', error)
        this.showErrorDialog('Failed to save asset: ' + (error.message || 'Unknown error'))
      }
    },
    showLog (item) {
      this.editObject = item
      this.showAssetLog = true
    },
    ...mapMutations(['showErrorDialog']),
    async saveRedemption (info) {
      try {
        if (info.single) {
          const res = await checkRedemption(info.code)
          if (res.isDuplicate) {
            this.showErrorDialog('该兑换码重复！')
            return
          }
        } else {
          info.count = parseFloat(info.count)
          info.code = null
        }
        if (info.expiresAt) {
          info.expiresAt = info.expiresAt + 'T00:00:00.000Z'
        }
        info.batchId = dayjs().valueOf()
        await createAssetRedemption(this.editObject.id, info)
        showSuccessMessage('兑换码创建成功！')
      } catch (error) {
        console.error('Error creating redemption code:', error)
        showErrorMessage(error.message || '创建兑换码失败')
      }
    },
    async createRedemption (item) {
      // Deep copy the item to avoid modifying the original
      this.editObject = IKUtils.deepCopy(item)

      const store = useBaseFormDialogStore()

      // Define the schema for the redemption code dialog
      const schema = {
        title: '创建兑换码',
        subtitle: `为 ${item.name || '资产'} 创建兑换码`,
        schemas: this.redemptionCodeSchemas
      }

      // Open the dialog and wait for the result
      const result = await store.editItem(schema)

      // Handle the result
      if (result) {
        await this.saveRedemption(result)
      }
    },
    async submitConfig (info) {
      try {
        if (this.editObject.assetType === 'VOUCHER') {
          delete info.point
          info.productCodes = info.productCodes ? info.productCodes.join(',') : ''
          info.activeSince = dayjs(info.activeSince).format('YYYY-MM-DD') + 'T00:00:00'
          info.expiredAt = dayjs(info.expiredAt).format('YYYY-MM-DD') + 'T00:00:00'
          this.editObject.assetConfig = JSON.stringify(info)
        } else if (this.editObject.assetType === 'POINT_EXCHANGE') {
          this.editObject.assetConfig = JSON.stringify({ point: info.point, type: this.editObject.assetType })
        } else {
          this.editObject.assetConfig = JSON.stringify({ type: this.editObject.assetType })
        }
        await Asset.edit(this.editObject)
        showSuccessMessage('配置更新成功')
      } catch (error) {
        console.error('Error updating asset configuration:', error)
        showErrorMessage(error.message || '更新配置失败')
      }
    },
    async editConfig (item) {
      // Deep copy the item to avoid modifying the original
      this.editObject = IKUtils.deepCopy(item)

      try {
        // Parse the asset configuration
        const editConfigInfo = item.assetConfig ? JSON.parse(item.assetConfig) : {}
        editConfigInfo.type = item.assetType

        // Process product codes for vouchers
        editConfigInfo.productCodes = editConfigInfo.productCodes ? editConfigInfo.productCodes.split(',') : []

        // Format dates
        editConfigInfo.activeSince = editConfigInfo.activeSince ? dayjs(editConfigInfo.activeSince).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
        editConfigInfo.expiredAt = editConfigInfo.expiredAt ? dayjs(editConfigInfo.expiredAt).format('YYYY-MM-DD') : dayjs().add(1, 'month').format('YYYY-MM-DD')

        const store = useBaseFormDialogStore()

        // Define the schema for the config dialog
        const schema = {
          title: '资产配置',
          subtitle: `配置 ${item.name || '资产'}`,
          schemas: this.configSchemas
        }

        // Open the dialog and wait for the result
        const result = await store.editItem(schema, editConfigInfo)

        // Handle the result
        if (result) {
          await this.submitConfig(result)
        }
      } catch (error) {
        console.error('Error parsing asset config:', error)
        showErrorMessage('解析资产配置失败: ' + (error.message || '未知错误'))
      }
    },
    async distributedVoucher (item) {
      this.editObject = item

      try {
        // Open the member selector dialog
        const selectedMemberIds = await openMemberSelectorDialog({
          title: `将 ${item.name || '资产'} 分发给用户`,
          multiple: true,
          initialSelection: [],
          returnMode: 'user'
        })

        if (selectedMemberIds.length === 0) {
          return
        }

        // Prompt for note
        const store = useBaseFormDialogStore()
        const noteSchema = {
          title: '分发备注',
          subtitle: `将 ${item.name || '资产'} 分发给 ${selectedMemberIds.length} 位用户`,
          schemas: [
            {
              key: 'note',
              name: '分发备注',
              hint: '分发资产的备注信息',
              required: false,
              componentProps: {
                placeholder: '请输入分发备注'
              }
            }
          ]
        }

        // Open the dialog and wait for the result
        const noteResult = await store.editItem(noteSchema)

        // Handle the result
        if (noteResult) {
          await this.saveDistribution({
            userIds: selectedMemberIds,
            note: noteResult.note
          })
        }
      } catch (error) {
        console.error('Error distributing asset:', error)
        // If the error is from the dialog being cancelled, don't show an error message
        if (error !== 'Selection cancelled') {
          showErrorMessage('分发资产失败: ' + (error.message || '未知错误'))
        }
      }
    },
    async saveDistribution (info) {
      console.log(info, 'info')
      try {
        const response = await distributeAssetToUsersList(this.editObject.id, info)

        // Check if the response indicates an error
        if (response && response.code && response.code !== 200) {
          throw new Error(response.message || '分发资产失败')
        }

        showSuccessMessage('资产分发成功')

        // Refresh the asset detail dialog if it's open
        if (this.showAssetDetailDialog && this.$refs.assetDetailDialog) {
          await this.$refs.assetDetailDialog.loadAsset()
        }
      } catch (error) {
        console.error('Error distributing asset:', error)
        showErrorMessage(error.message || '分发资产失败')
        throw error // Re-throw the error so it can be caught by the caller
      }
    },
    async reload () {
      this.allDishes = await Dish.getList(true)
      this.allMemberUser = await UserMembership.getList(true)
    }
  }
}
</script>

<template>
  <v-container>
    <ik-data-table
      ref="table"
      :custom-on-row-click="openAssetDetail"
      :model="model"
      :use-default-action="false"
      :use-custom-action="false"
      entity-name="资产"
      icon="mdi-cash-usd"
    >
      <template #primaryButton>
        <v-btn
          color="amber lighten-4 black--text"
          elevation="0"
          @click="createNewAsset"
        >
          <v-icon left>
            mdi-plus
          </v-icon>
          {{ $t('add') }}
        </v-btn>
      </template>
      <template #item.imageDisplay="{item}">
        <div style="display: flex;">
          <img
            v-if="item.image"
            :src="item.image"
            alt="Image"
            style="width: 25px; height: 25px; margin-right: 5px;"
          >
        </div>
      </template>
    </ik-data-table>

    <!-- Asset Detail Dialog -->
    <asset-detail-dialog
      ref="assetDetailDialog"
      v-model="showAssetDetailDialog"
      :asset-id="selectedAssetId"
      @edit="handleEditAsset"
      @distribute="handleDistributeAsset"
      @create-redemption="handleCreateRedemption"
      @deleted="handleAssetDeleted"
    />
  </v-container>
</template>

<style scoped>

</style>
